import { FC } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import percentSvg from 'assets/icons/percent.svg';
import { Store } from 'components/common/types/Store.types';
import SectionButton, { SectionButtonTheme } from 'lib/SectionButton';
import { PlannerSecondaryPanelType } from 'store/dealManagement/types';
import { useOnChangePanelVisibility } from 'components/pages/PCM/hooks/useOnChangePanelVisibility';
import { changeNewProductField, clearItemInNewProduct, hideSecondaryPanel } from 'store/productManagement/reducer';
import { ProductFieldName } from 'store/productManagement/reducer.types';
import { getIsReadOnly } from 'store/productManagement/selectors';
import { ProductShareOfTimeValues } from './ProductShareOfTime.types';
import ProductShareOfTimePanel from './ProductShareOfTimePanel';
import { buildSelectedValue } from './utils';

const ProductShareOfTime: FC = () => {
  const dispatch = useDispatch();
  const isReadOnly = useSelector(getIsReadOnly);
  const mediaType = useSelector((state: Store) => state.productManagement.newProduct.mediaType?.value);
  const channels = useSelector((state: Store) => state.productManagement.newProduct.channels?.value);
  const secondaryPanelType = useSelector((state: Store) => state.productManagement.secondaryPanel);
  const sot = useSelector((state: Store) => state.productManagement.newProduct.sot);
  const sweep = useSelector((state: Store) => state.productManagement.newProduct.sweep);

  const onChangePanelVisibility = useOnChangePanelVisibility();

  const isProductShareOfTimePanelOpen = secondaryPanelType === PlannerSecondaryPanelType.SOT;

  const handleConfirm = ({
    sotValue,
    sotSelectedPermission,
    sweepValue,
    sweepSelectedPermission,
  }: ProductShareOfTimeValues): void => {
    dispatch(
      changeNewProductField({
        fieldName: ProductFieldName.SOT,
        value: sotValue,
        selectedPermission: sotSelectedPermission,
      }),
    );

    dispatch(
      changeNewProductField({
        fieldName: ProductFieldName.SWEEP,
        value: sweepValue,
        selectedPermission: sweepSelectedPermission,
      }),
    );

    dispatch(hideSecondaryPanel());
  };

  const selectedValues = buildSelectedValue({ sot, sweep });

  const onRemoveShareOfTimeChip = (shareOfTimePartsToRemove?: string[]): void => {
    if (!shareOfTimePartsToRemove) {
      dispatch(clearItemInNewProduct({ fieldName: ProductFieldName.SOT }));
      dispatch(clearItemInNewProduct({ fieldName: ProductFieldName.SWEEP }));
      return;
    }

    if (!shareOfTimePartsToRemove.length) return;

    const shareOfTimePartsSet = new Set(shareOfTimePartsToRemove);

    if (shareOfTimePartsSet.has('Sweep') || shareOfTimePartsSet.has('Sweep type')) {
      dispatch(clearItemInNewProduct({ fieldName: ProductFieldName.SWEEP }));
    } else {
      dispatch(clearItemInNewProduct({ fieldName: ProductFieldName.SOT }));
    }
  };

  return (
    <>
      <SectionButton
        dataTestId="product-share-of-time"
        label="Share of time"
        icon={percentSvg}
        isDisabled={!mediaType || !channels?.length}
        isEllipsisDisabled={isReadOnly}
        theme={Object.keys(selectedValues).length > 0 ? SectionButtonTheme.SOLID : SectionButtonTheme.DASHED}
        onClick={() => onChangePanelVisibility(PlannerSecondaryPanelType.SOT)}
        onClear={() => {
          dispatch(clearItemInNewProduct({ fieldName: ProductFieldName.SOT }));
          dispatch(clearItemInNewProduct({ fieldName: ProductFieldName.SWEEP }));
          dispatch(hideSecondaryPanel());
        }}
        onRemoveChip={onRemoveShareOfTimeChip}
        selected={selectedValues}
      />
      {isProductShareOfTimePanelOpen && <ProductShareOfTimePanel onConfirm={handleConfirm} isReadOnly={isReadOnly} />}
    </>
  );
};

export default ProductShareOfTime;
